import React from 'react';
import { motion } from 'framer-motion';
import {
    Hash,
    Mail,
    Calendar,
    ListTodo,
    FileEdit,
    StickyNote,
    FolderOpen,
    Search,
    Webhook,
    Phone,
    ShieldCheck,
    Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// DATA
// ============================================================================

const channels = [
    {
        icon: Phone,
        nameKey: 'integrations.channels.sms.name',
        descKey: 'integrations.channels.sms.desc',
        highlights: [
            'integrations.channels.sms.h1',
            'integrations.channels.sms.h2',
            'integrations.channels.sms.h3',
        ],
        status: 'live',
        accentColor: 'rgba(48, 209, 88, 0.7)',
        glowColor: 'rgba(48, 209, 88, 0.08)',
    },
    {
        icon: Hash,
        nameKey: 'integrations.channels.slack.name',
        descKey: 'integrations.channels.slack.desc',
        highlights: [
            'integrations.channels.slack.h1',
            'integrations.channels.slack.h2',
            'integrations.channels.slack.h3',
        ],
        status: 'live',
        accentColor: 'rgba(10, 132, 255, 0.7)',
        glowColor: 'rgba(10, 132, 255, 0.08)',
    },
    {
        icon: Mail,
        nameKey: 'integrations.channels.email.name',
        descKey: 'integrations.channels.email.desc',
        highlights: [
            'integrations.channels.email.h1',
            'integrations.channels.email.h2',
            'integrations.channels.email.h3',
        ],
        status: 'live',
        accentColor: 'rgba(255, 159, 10, 0.7)',
        glowColor: 'rgba(255, 159, 10, 0.08)',
    },
];

const mcpTools = [
    { icon: Calendar, nameKey: 'integrations.tools.calendar' },
    { icon: ListTodo, nameKey: 'integrations.tools.tasks' },
    { icon: FileEdit, nameKey: 'integrations.tools.email_drafting' },
    { icon: StickyNote, nameKey: 'integrations.tools.notes' },
    { icon: FolderOpen, nameKey: 'integrations.tools.files' },
    { icon: Search, nameKey: 'integrations.tools.docs_search' },
];

const webhookEvents = [
    'escalation.created',
    'escalation.resolved',
    'replica.activated',
    'replica.suspended',
    'training.completed',
    'decision.approved',
    'decision.rejected',
];

// ============================================================================
// COMPONENT
// ============================================================================

const IntegrationsShowcase: React.FC = () => {
    const { t } = useTranslation('marketing');

    return (
        <section className="relative py-24 md:py-32 overflow-hidden" id="integrations">
            {/* Static separator line */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-accent/20 to-transparent" />

            <div className="container-hig relative">
                {/* ── Section Header ─────────────────────────────────── */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="inline-block px-4 py-2 glass-tinted text-white/80 text-sm font-medium mb-6 rounded-full">
                        {t('integrations.badge')}
                    </div>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                        {t('integrations.title')}
                    </h2>
                    <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                        {t('integrations.subtitle')}
                    </p>
                </motion.div>

                {/* ── Communication Channels ──────────────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                    {channels.map((channel, index) => {
                        const IconComponent = channel.icon;
                        return (
                            <motion.div
                                key={index}
                                className="relative group card-surface p-7 md:p-8 transition-colors"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.5, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ borderColor: 'rgba(10,132,255,0.15)', y: -4, transition: { duration: 0.3 } }}
                            >
                                {/* Hover glow */}
                                <div
                                    className="absolute -inset-1 rounded-2xl pointer-events-none blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                    style={{ background: `radial-gradient(circle at 50% 50%, ${channel.glowColor}, transparent 70%)` }}
                                />

                                {/* Header: Icon + Status badge */}
                                <div className="relative flex items-center justify-between mb-5">
                                    <div className="w-10 h-10 icon-box">
                                        <IconComponent className="w-5 h-5" style={{ color: channel.accentColor }} />
                                    </div>
                                    <span
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide"
                                        style={{
                                            background: channel.status === 'live'
                                                ? 'rgba(48, 209, 88, 0.1)'
                                                : 'rgba(255, 159, 10, 0.1)',
                                            color: channel.status === 'live'
                                                ? 'rgba(48, 209, 88, 0.8)'
                                                : 'rgba(255, 159, 10, 0.8)',
                                            border: `1px solid ${channel.status === 'live'
                                                ? 'rgba(48, 209, 88, 0.2)'
                                                : 'rgba(255, 159, 10, 0.2)'}`,
                                        }}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full" style={{
                                            background: channel.status === 'live'
                                                ? 'rgba(48, 209, 88, 0.9)'
                                                : 'rgba(255, 159, 10, 0.9)',
                                        }} />
                                        {channel.status === 'live' ? t('integrations.status_live') : t('integrations.status_ready')}
                                    </span>
                                </div>

                                {/* Name */}
                                <h3 className="relative text-base font-semibold text-white mb-2">
                                    {t(channel.nameKey)}
                                </h3>

                                {/* Description */}
                                <p className="relative text-sm text-white/40 leading-relaxed mb-5">
                                    {t(channel.descKey)}
                                </p>

                                {/* Highlights */}
                                <ul className="relative space-y-2">
                                    {channel.highlights.map((hKey, i) => (
                                        <li key={i} className="flex items-start gap-2.5">
                                            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: channel.accentColor }} />
                                            <span className="text-xs text-white/50 leading-relaxed">
                                                {t(hKey)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>

                {/* ── MCP Tools ───────────────────────────────────────── */}
                <motion.div
                    className="mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 icon-box rounded-lg">
                            <Zap className="w-4 h-4 text-hig-accent/70" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">
                            {t('integrations.mcp_title')}
                        </h3>
                        <span className="text-xs text-white/30">
                            {t('integrations.mcp_subtitle')}
                        </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        {mcpTools.map((tool, index) => {
                            const ToolIcon = tool.icon;
                            return (
                                <motion.div
                                    key={index}
                                    className="group relative card-surface-interactive p-4 text-center"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: 0.05 * index }}
                                    whileHover={{ borderColor: 'rgba(10,132,255,0.2)', y: -2, transition: { duration: 0.2 } }}
                                >
                                    <div className="w-9 h-9 mx-auto icon-box rounded-lg mb-2.5">
                                        <ToolIcon className="w-4 h-4 text-hig-accent/60" />
                                    </div>
                                    <span className="text-xs font-medium text-white/60 group-hover:text-white/80 transition-colors">
                                        {t(tool.nameKey)}
                                    </span>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* ── Webhooks Callout ────────────────────────────────── */}
                <motion.div
                    className="relative card-surface p-7 md:p-8 overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    whileHover={{ borderColor: 'rgba(10,132,255,0.12)', transition: { duration: 0.3 } }}
                >
                    {/* Subtle gradient accent */}
                    <div
                        className="absolute top-0 right-0 w-64 h-64 pointer-events-none opacity-[0.04]"
                        style={{ background: 'radial-gradient(circle, rgba(10,132,255,1), transparent 70%)' }}
                    />

                    <div className="relative flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                        {/* Left: description */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 icon-box">
                                    <Webhook className="w-5 h-5 text-hig-accent/70" />
                                </div>
                                <h3 className="text-base font-semibold text-white">
                                    {t('integrations.webhooks.title')}
                                </h3>
                            </div>
                            <p className="text-sm text-white/40 leading-relaxed mb-4">
                                {t('integrations.webhooks.desc')}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-hig-accent/70 font-medium">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                {t('integrations.webhooks.security')}
                            </div>
                        </div>

                        {/* Right: event badges */}
                        <div className="flex-shrink-0 md:w-[280px]">
                            <p className="text-xs font-medium text-white/30 uppercase tracking-wider mb-3">
                                {t('integrations.webhooks.events_label')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {webhookEvents.map((event, i) => (
                                    <motion.span
                                        key={event}
                                        className="inline-flex px-2.5 py-1 rounded-lg text-[11px] font-mono text-white/50 bg-white/[0.04] border border-white/[0.06]"
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.25, delay: 0.03 * i }}
                                    >
                                        {event}
                                    </motion.span>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ── Bottom Tagline ──────────────────────────────────── */}
                <motion.p
                    className="mt-10 text-center text-xs text-white/20"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    {t('integrations.bottom_tagline')}
                </motion.p>
            </div>
        </section>
    );
};

export default IntegrationsShowcase;
